import React, { useState } from 'react';
import { Users, Calendar, ExternalLink, MessageCircle, MapPin, Clock, TrendingUp } from 'lucide-react';

const ConceptDirectory = () => {
  const [selectedCommunity, setSelectedCommunity] = useState('hiking');

  const communities = {
    hiking: {
      name: 'Hiking & Outdoor',
      members: 234,
      description: 'Join fellow outdoor enthusiasts for weekly hikes, camping trips, and mountain adventures across Switzerland.',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=400&fit=crop',
      links: [
        { type: 'discord', label: 'Discord Server', url: '#', icon: MessageCircle },
        { type: 'whatsapp', label: 'WhatsApp Group', url: '#', icon: MessageCircle },
        { type: 'website', label: 'Website', url: '#', icon: ExternalLink }
      ],
      upcomingEvents: [
        { date: 'Nov 8', title: 'Pilatus Sunrise Hike', location: 'Lucerne', attendees: 12 },
        { date: 'Nov 15', title: 'Rhine Falls Walk', location: 'Schaffhausen', attendees: 8 },
        { date: 'Nov 22', title: 'Winterthur Forest Trail', location: 'Winterthur', attendees: 15 }
      ]
    },
    food: {
      name: 'Food & Dining',
      members: 189,
      description: 'Discover new restaurants, share recipes, and organize food tours and cooking classes together.',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=400&fit=crop',
      links: [
        { type: 'discord', label: 'Discord Server', url: '#', icon: MessageCircle },
        { type: 'instagram', label: 'Instagram', url: '#', icon: ExternalLink }
      ],
      upcomingEvents: [
        { date: 'Nov 10', title: 'Fondue Night', location: 'Zurich Old Town', attendees: 20 },
        { date: 'Nov 17', title: 'Street Food Tour', location: 'Basel', attendees: 14 },
        { date: 'Nov 24', title: 'Cooking Class: Swiss Cuisine', location: 'Bern', attendees: 10 }
      ]
    },
    tech: {
      name: 'Tech & Innovation',
      members: 312,
      description: 'Connect with developers, entrepreneurs, and tech enthusiasts. Share projects, attend hackathons, and network.',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop',
      links: [
        { type: 'discord', label: 'Discord Server', url: '#', icon: MessageCircle },
        { type: 'slack', label: 'Slack Workspace', url: '#', icon: MessageCircle },
        { type: 'github', label: 'GitHub Org', url: '#', icon: ExternalLink }
      ],
      upcomingEvents: [
        { date: 'Nov 9', title: 'AI Meetup', location: 'Zurich ETH', attendees: 45 },
        { date: 'Nov 16', title: 'Startup Pitch Night', location: 'Geneva', attendees: 30 },
        { date: 'Nov 23', title: 'Hackathon Weekend', location: 'Lausanne', attendees: 60 }
      ]
    },
    fitness: {
      name: 'Fitness & Sports',
      members: 267,
      description: 'Stay active together! Join group runs, yoga sessions, cycling tours, and sports tournaments.',
      image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=400&fit=crop',
      links: [
        { type: 'whatsapp', label: 'WhatsApp Group', url: '#', icon: MessageCircle },
        { type: 'strava', label: 'Strava Club', url: '#', icon: ExternalLink }
      ],
      upcomingEvents: [
        { date: 'Nov 7', title: 'Morning Yoga by the Lake', location: 'Zurich Lake', attendees: 18 },
        { date: 'Nov 14', title: 'Group Cycling Tour', location: 'Rhine Valley', attendees: 22 },
        { date: 'Nov 21', title: 'Beach Volleyball', location: 'Bern', attendees: 16 }
      ]
    }
  };

  const community = communities[selectedCommunity];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="w-8 h-8 text-red-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SwissMeet</h1>
                <p className="text-sm text-gray-500">Community Directory</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Concept B:</span> Directory View
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6">
          {/* Left Sidebar - Communities List */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Communities</h2>
              <div className="space-y-2">
                {Object.entries(communities).map(([key, comm]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCommunity(key)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      selectedCommunity === key
                        ? 'bg-red-50 border-2 border-red-500 text-red-700'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{comm.name}</div>
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <Users className="w-3 h-3 mr-1" />
                      {comm.members} members
                    </div>
                  </button>
                ))}
              </div>

              <button className="w-full mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                + Create Community
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Community Header */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${community.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-6">
                  <h2 className="text-3xl font-bold text-white">{community.name}</h2>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-white/90 flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {community.members} members
                    </span>
                    <span className="text-white/90 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      Growing
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">About</h3>
                <p className="text-gray-600 mb-4">{community.description}</p>

                <div className="flex items-center space-x-3">
                  <button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                    Join Community
                  </button>
                  <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                    Share
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Direct Links */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <ExternalLink className="w-5 h-5 mr-2 text-red-600" />
                  Direct Links
                </h3>
                <div className="space-y-3">
                  {community.links.map((link, idx) => {
                    const Icon = link.icon;
                    return (
                      <a
                        key={idx}
                        href={link.url}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <Icon className="w-5 h-5 text-red-600" />
                          </div>
                          <span className="font-medium text-gray-900">{link.label}</span>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-red-600" />
                  Upcoming Events
                </h3>
                <div className="space-y-3">
                  {community.upcomingEvents.map((event, idx) => (
                    <div
                      key={idx}
                      className="p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{event.title}</div>
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <MapPin className="w-3 h-3 mr-1" />
                            {event.location}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                          {event.date}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          {event.attendees} going
                        </span>
                        <button className="text-red-600 hover:text-red-700 font-medium">
                          Join →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button className="w-full mt-4 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium">
                  View All Events
                </button>
              </div>
            </div>

            {/* Mini Chat Preview */}
            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <MessageCircle className="w-5 h-5 mr-2 text-red-600" />
                Community Chat
              </h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                    A
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">Anna M.</span>
                      <span className="text-sm text-gray-500">2 min ago</span>
                    </div>
                    <p className="text-gray-700 mt-1">Anyone up for a coffee meetup tomorrow?</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-medium">
                    M
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">Marco S.</span>
                      <span className="text-sm text-gray-500">5 min ago</span>
                    </div>
                    <p className="text-gray-700 mt-1">I'm in! Where should we meet?</p>
                  </div>
                </div>
              </div>
              <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                Open Full Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptDirectory;
